import { ReactiveControllerHost } from "lit";
import { isEqual } from "lodash-es";
import { PersistedReduxController } from "../../common/redux/controllers/persisted-redux-controller";
import { AuthActionPayload } from "../redux/auth-slice/types";
import {
  RegisterRequestData,
  UpdateUserRequestData,
} from "../redux/services/api/types";
import {
  RootState,
  persistor,
  store,
  slices,
  selectors,
  actions,
  userAPI,
} from "../redux/store";
import { ApiController } from "./api-controller";
import { User } from "../types";
import {
  getClientId,
  getGBRAID,
  getGCLID,
  getWBRAID,
} from "../../utils/google-cookies";

export class AuthController extends PersistedReduxController<RootState> {
  apiController: ApiController;

  constructor(host: ReactiveControllerHost) {
    super(host, store, persistor);
    this.apiController = new ApiController(host);
  }

  get authState() {
    return this.state[slices.auth.name];
  }

  get subscriptionState() {
    return this.state[slices.subscription.name];
  }

  get user() {
    return selectors.auth.getUser(this.authState);
  }

  get authCredentials() {
    return selectors.auth.credentials(this.authState);
  }

  get token() {
    return this.authCredentials.token;
  }

  get uuid() {
    return this.authCredentials.uuid;
  }

  get isLogged() {
    return selectors.auth.isLogged(this.authState);
  }

  override stateChanged(state: RootState, forceUpdate: boolean): void {
    if (forceUpdate || !isEqual(state[slices.auth.name], this.authState)) {
      this.updateState(state);
      this.host.requestUpdate();
    }
  }

  onAuthStateChanged(
    callback: (state: { isLogged: boolean; user: User | null }) => void
  ) {
    return store.subscribe(() => {
      const state = store.getState();
      const authState = state[slices.auth.name];
      callback({
        isLogged: selectors.auth.isLogged(authState),
        user: selectors.auth.getUser(authState),
      });
    });
  }

  login(payload: AuthActionPayload) {
    return this.dispatch(actions.auth.login(payload));
  }

  logout() {
    return this.dispatch(actions.auth.logout());
  }

  register(data: RegisterRequestData) {
    const authData = this.getAuthRequestData();
    return store.dispatch(
      userAPI.endpoints.register.initiate({
        ...authData,
        data,
      })
    );
  }

  updateUser(data: UpdateUserRequestData) {
    const authData = this.getAuthRequestData();
    return store.dispatch(
      userAPI.endpoints.updateUser.initiate({
        ...authData,
        data,
      })
    );
  }

  deleteAccount() {
    const authData = this.getAuthRequestData();
    return store.dispatch(userAPI.endpoints.deleteAccount.initiate(authData));
  }

  registerStatus(requestId?: string) {
    return this.apiController.getRequestStatus(
      userAPI.endpoints.register.name,
      requestId
    );
  }

  updateUserStatus(requestId?: string) {
    return this.apiController.getRequestStatus(
      userAPI.endpoints.updateUser.name,
      requestId
    );
  }

  deleteAccountStatus(requestId?: string) {
    return this.apiController.getRequestStatus(
      userAPI.endpoints.deleteAccount.name,
      requestId
    );
  }

  getAuthRequestData() {
    return selectors.auth.credentials(this.authState);
  }

  async sendPageView() {
    const authData = this.getAuthRequestData();
    const promise = store.dispatch(
      userAPI.endpoints.sendPageView.initiate({
        ...authData,
        data: {
          url: window.location.pathname,
        },
      })
    );

    promise.then(() => {
      store.dispatch(
        userAPI.endpoints.getPendingNotifications.initiate(authData)
      );
    });

    return promise;
  }

  async addTransactionTracking() {
    const authData = this.getAuthRequestData();
    const clientId = await getClientId();
    const gclid = getGCLID();
    const gbraid = getGBRAID();
    const wbraid = getWBRAID();

    if (!this.isLogged) {
      return null;
    }

    const promise = store.dispatch(
      userAPI.endpoints.addTransactionTracking.initiate({
        ...authData,
        client_id: clientId,
        gclid: gclid,
        gbraid: gbraid,
        wbraid: wbraid,
      })
    );

    return promise;
  }
}
